import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  Bar,
} from 'recharts';
import { balanceMap } from './util';

/**
 * ABTestingPanel
 * Displays the A/B test configuration and summary for a model.
 */
function ABTestingPanel({
  abTestConfig,
  setAbTestConfig,
  abConfirmOpen,
  setAbConfirmOpen,
  abPendingAction,
  setAbPendingAction,
  handleConfirmABConfig,
  modelId,
  statsData,
  statsLoading,
}) {
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleConfirmClick = async () => {
    if (!handleConfirmABConfig) return;
    setConfirmLoading(true);
    try {
      await handleConfirmABConfig();
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      {/* Panel Header */}
      <Box display='flex' justifyContent='flex-start' mb={2}>
        <Typography variant='h6'>A/B Testing</Typography>
      </Box>

      {/* ================= CONFIGURATION DROPDOWN ================= */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 3,
          position: 'relative',
        }}
      >
        <Button
          variant='contained'
          sx={{ minWidth: 200 }}
          onClick={() =>
            setAbTestConfig((prev) => ({
              ...prev,
              dropdownOpen: !prev?.dropdownOpen,
            }))
          }
        >
          A/B Configuration
        </Button>

        {abTestConfig?.dropdownOpen && (
          <Paper
            sx={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: '50%',
              transform: 'translateX(-50%)',
              p: 3,
              width: 600,
              display: 'flex',
              gap: 3,
              zIndex: 10,
              boxShadow: 3,
            }}
          >
            {/* Left Column: Status & Progress */}
            <Box sx={{ flex: 1 }}>
              <Typography variant='subtitle1' mb={1}>
                Status
              </Typography>
              <Box display='flex' gap={1} mb={2}>
                {['Enabled', 'Disabled'].map((status) => (
                  <Tooltip
                    key={status}
                    title={
                      status === 'Enabled'
                        ? 'A/B testing is active for this model'
                        : 'A/B testing is currently disabled'
                    }
                    arrow
                  >
                    <Button
                      size='small'
                      fullWidth
                      variant={
                        (abTestConfig?.enabled ? 'Enabled' : 'Disabled') ===
                        status
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() => {
                        const enabled = status === 'Enabled';
                        setAbPendingAction(enabled ? 'enable' : 'disable');
                        setAbConfirmOpen(true);
                      }}
                    >
                      {status}
                    </Button>
                  </Tooltip>
                ))}
              </Box>

              {/* Traffic Split Progress */}
              <Box display='flex' justifyContent='space-between' mb={1}>
                <Typography variant='body2'>
                  Predict {abTestConfig?.predictPercent ?? 0}%
                </Typography>
                <Typography variant='body2'>
                  Test {abTestConfig?.testPercent ?? 0}%
                </Typography>
              </Box>
              <Box
                sx={{ height: 10, backgroundColor: '#eee', borderRadius: 2 }}
              >
                <Box
                  sx={{
                    width: `${abTestConfig?.predictPercent ?? 0}%`,
                    height: '100%',
                    backgroundColor: '#6a1b9a',
                    borderRadius: 2,
                  }}
                />
              </Box>
            </Box>

            {/* Right Column: Traffic Options */}
            <Box sx={{ flex: 1, borderLeft: '1px solid #ccc', pl: 3 }}>
              <Typography variant='subtitle1' mb={1}>
                Traffic Split Options
              </Typography>
              <Box display='flex' flexDirection='column' gap={1}>
                {Object.entries(balanceMap).map(([label, value]) => (
                  <Button
                    key={label}
                    variant={
                      abTestConfig?.trafficMode === label
                        ? 'contained'
                        : 'outlined'
                    }
                    onClick={() =>
                      setAbTestConfig((prev) => ({
                        ...prev,
                        trafficMode: label,
                        predictPercent: value,
                        testPercent: 100 - value,
                      }))
                    }
                  >
                    {label}: {value}% prediction
                  </Button>
                ))}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* ================= CONFIRM MODAL ================= */}
      {abConfirmOpen && (
        <Paper
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.4)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => setAbConfirmOpen(false)}
        >
          <Paper
            sx={{ p: 3, width: 350, textAlign: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant='h6' mb={2}>
              Confirm Action
            </Typography>
            <Typography mb={3}>
              Are you sure you want to{' '}
              <strong>
                {abPendingAction === 'enable' ? 'ENABLE' : 'DISABLE'}
              </strong>{' '}
              A/B Testing
            </Typography>
            <Box display='flex' justifyContent='space-between' mt={2}>
              <Button
                variant='outlined'
                onClick={() => setAbConfirmOpen(false)}
                disabled={confirmLoading}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                color={abPendingAction === 'enable' ? 'success' : 'error'}
                onClick={handleConfirmClick}
                disabled={confirmLoading}
              >
                {confirmLoading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  'Confirm'
                )}
              </Button>
            </Box>
          </Paper>
        </Paper>
      )}

      {/* ================= A/B Test Results ================= */}
      <Box sx={{ mb: 3 }}>
        <Typography variant='subtitle1' mb={1}>
          A/B Test Results
        </Typography>

        {statsLoading ? (
          <Box display='flex' justifyContent='center' py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : statsData.length > 0 ? (
          <ResponsiveContainer width='100%' height={300}>
            <BarChart
              data={statsData}
              margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
            >
              <XAxis
                dataKey='group'
                label={{ value: 'Group', position: 'bottom', offset: 10 }}
              />
              <YAxis
                tickFormatter={(value) => `${value.toFixed(0)}%`}
                domain={[0, 100]}
                label={{
                  value: 'Percentage (%)',
                  angle: -90,
                  position: 'outsideLeft',
                  dx: -30,
                }}
              />
              <RechartsTooltip formatter={(value) => `${value.toFixed(1)}%`} />
              <Legend verticalAlign='top' height={36} />
              <Bar
                dataKey='ReachedTarget'
                fill='#7b68ee'
                label={{
                  position: 'top',
                  formatter: (value) => `${value.toFixed(1)}%`,
                }}
              />
              <Bar
                dataKey='NotReachedTarget'
                fill='#b19cd9'
                label={{
                  position: 'top',
                  formatter: (value) => `${value.toFixed(1)}%`,
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography color='text.secondary'>
            No A/B test statistics available
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

export default ABTestingPanel;
